/**
 * getConversationSendContext - Contexto de envio de uma conversa
 *
 * Helper único usado por telas FORA do chat (ex.: compartilhar proposta em PDF)
 * para descobrir os dados mínimos necessários ao envio de uma mensagem/arquivo:
 * inbox, destino (telefone do contato OU external_id do grupo), slug do provider
 * e canal da inbox.
 *
 * Regras:
 *   - NÃO faz SELECT direto em `conversations`/`contacts`/`inboxes`: toda a
 *     leitura passa pela RPC SECURITY DEFINER `get_conversation_send_context`,
 *     que valida tenant + acesso do usuário à conversa e registra tentativas
 *     indevidas em `security_bypass_attempts`;
 *   - suporta conversas 1:1 e de grupo (multi-provider: o slug retornado é o
 *     mesmo consumido pela edge function `send-message`).
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.0.0 - Versão inicial (substitui o select ad-hoc do
 *            ProposalShareDialog).
 */

import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('getConversationSendContext');

/** Contexto retornado pela RPC, já normalizado para o frontend */
export interface ConversationSendContext {
  conversationId: string;
  inboxId: string;
  contactId: string | null;
  groupId: string | null;
  /** Telefone do contato (1:1) ou identificador externo do grupo */
  destination: string | null;
  providerSlug: string | null;
  channelType: string | null;
  inboxStatus: string | null;
  isGroup: boolean;
}

/**
 * Busca o contexto de envio de uma conversa.
 * Lança exceção quando a conversa não existe, é de outro tenant ou o usuário
 * não tem acesso (mensagem vinda da própria RPC).
 */
export async function getConversationSendContext(
  conversationId: string,
  tenantId?: string | null,
): Promise<ConversationSendContext> {
  const { data, error } = await supabase.rpc('get_conversation_send_context' as any, {
    p_conversation_id: conversationId,
    p_tenant_hint: tenantId ?? null,
  } as any);

  if (error) {
    logger.error('Erro ao carregar contexto de envio da conversa', {
      conversationId,
      error: error.message,
    });
    throw new Error(error.message || 'Não foi possível carregar a conversa para envio.');
  }

  const row = (Array.isArray(data) ? data[0] : data) as Record<string, any> | undefined;

  if (!row) {
    throw new Error('Conversa não encontrada para envio.');
  }

  return {
    conversationId: row.conversation_id,
    inboxId: row.inbox_id,
    contactId: row.contact_id ?? null,
    groupId: row.group_id ?? null,
    destination: row.destination ?? null,
    providerSlug: row.provider_slug ?? null,
    channelType: row.channel_type ?? null,
    inboxStatus: row.inbox_status ?? null,
    isGroup: Boolean(row.is_group),
  };
}
